import * as Yup from 'yup'
import {
  awsAccountIdFieldValidationMessage,
  casS3ValidationMessage,
  casSNSTopicValidationMessage,
  customerWebsiteValidationMessage,
  iamArnValidationMessage,
  kmsKeyArnValidationMessage,
  mandatoryFieldMessage,
  sellerCountryOriginValidationMessage,
  websiteValidationMessage,
  s3ArnisMandatoryWhenOtherFieldsArepresent,
  kmsKeyArnisMandatoryWhenOtherFieldsArepresent,
  snsTopicArnisMandatoryWhenOtherFieldsArepresent,
  terraformConnectionValidationMandatoryMessage,
  awsRegexValidationMessage,
} from './messagesContants'

import {
  IamArnRegex,
  kmsKeyArnRegex,
  awsCasS3BucketNameRegex,
  emptySpaceCharsAtStartAndEndRegex,
  snsTopicARNPattern,
  awsAccountIdRegex,
  customerWebsiteUrlPattern,
} from './constants'
import { FlyOutListingUserType } from '../../types/external'
import { isEmpty } from 'lodash'

export const labraPartnerDetailsSchema = Yup.object({
  companyName: Yup.string().required(mandatoryFieldMessage),
  sellerOriginCountry: Yup.string()
    .required(sellerCountryOriginValidationMessage)
    .nullable(),
  companyWebsite: Yup.string()
    .matches(customerWebsiteUrlPattern, customerWebsiteValidationMessage)
    .required(websiteValidationMessage),

  partnershipType: Yup.array().when('listingType', {
    is: FlyOutListingUserType.MANAGED,
    then: Yup.array()
      .of(Yup.string())
      .min(1, 'At least one partnership type is required'),
    otherwise: Yup.array()
      .of(Yup.string())
      .min(1, 'At least one partnership type is required'),
  }),
  industry: Yup.string().required('Industry is required'),
})

export const getCloudSettingsSchema = (
  isMarketplaceCloudSettingsRevampEnabled = false,
  isOnboardingImprovementsEnabled = false
) =>
  Yup.object({
    cloudAccountId: Yup.string()
      .required(mandatoryFieldMessage)
      .matches(emptySpaceCharsAtStartAndEndRegex, `${mandatoryFieldMessage}.`)
      .min(12, awsAccountIdFieldValidationMessage)
      .max(12, awsAccountIdFieldValidationMessage)
      .matches(awsAccountIdRegex, awsRegexValidationMessage),
    cloudAccountName: Yup.string()
      .required(mandatoryFieldMessage)
      .matches(emptySpaceCharsAtStartAndEndRegex, `${mandatoryFieldMessage}.`),
    cloudRoleArn: Yup.string()
      .matches(emptySpaceCharsAtStartAndEndRegex, `${mandatoryFieldMessage}.`)
      .matches(IamArnRegex, iamArnValidationMessage)
      .required(mandatoryFieldMessage),
    connectionTested: Yup.boolean().test(
      'is true',
      'connection tested must be true',
      function (value) {
        if (
          this.parent.stackType === 'terraform' &&
          isOnboardingImprovementsEnabled
        ) {
          return true
        }
        return value === true
      }
    ),
    dataFeedsConnectionTested: isMarketplaceCloudSettingsRevampEnabled
      ? Yup.boolean().test(
          'is true',
          's3 bucket connection tested must be true',
          function (value) {
            if (
              this.parent.stackType === 'terraform' &&
              isOnboardingImprovementsEnabled
            ) {
              return true
            }
            if (this.parent.isOwnS3Bucket !== true) {
              return true
            }
            return value === true
          }
        )
      : Yup.boolean().optional(),
    terraformConnectionValidated: Yup.boolean().test(
      'terraform-connection-validated',
      terraformConnectionValidationMandatoryMessage,
      function (value) {
        if (
          this.parent.stackType !== 'terraform' ||
          !isOnboardingImprovementsEnabled
        ) {
          return true
        }
        return value === true
      }
    ),
    stackType: Yup.string().oneOf(['cloudformation', 'terraform']).optional(),
    cloudCasS3BucketName: isMarketplaceCloudSettingsRevampEnabled
      ? Yup.string()
          .matches(
            emptySpaceCharsAtStartAndEndRegex,
            `${mandatoryFieldMessage}.`
          )
          .required(s3ArnisMandatoryWhenOtherFieldsArepresent)
          .matches(awsCasS3BucketNameRegex, casS3ValidationMessage)
      : Yup.string()
          .matches(
            emptySpaceCharsAtStartAndEndRegex,
            `${mandatoryFieldMessage}.`
          )
          .matches(awsCasS3BucketNameRegex, casS3ValidationMessage)
          .test(
            'validateS3Bucket',
            s3ArnisMandatoryWhenOtherFieldsArepresent,
            (value, context) => {
              if (!isEmpty(value)) {
                return true
              } else {
                if (
                  !isEmpty(
                    context.options.context?.sellerMetadata?.awsCasSnsTopic
                  ) ||
                  !isEmpty(
                    context.options.context?.sellerMetadata?.awsKmsKeyArn
                  )
                ) {
                  return false
                }
                return true
              }
            }
          ),
    cloudKmsKeyArn: isMarketplaceCloudSettingsRevampEnabled
      ? Yup.string()
          .matches(
            emptySpaceCharsAtStartAndEndRegex,
            `${mandatoryFieldMessage}.`
          )
          .required(kmsKeyArnisMandatoryWhenOtherFieldsArepresent)
          .matches(kmsKeyArnRegex, kmsKeyArnValidationMessage)
      : Yup.string()
          .matches(kmsKeyArnRegex, kmsKeyArnValidationMessage)
          .test(
            'validateKmsKey',
            kmsKeyArnisMandatoryWhenOtherFieldsArepresent,
            (value, context) => {
              if (!isEmpty(value)) {
                return true
              } else {
                if (
                  !isEmpty(
                    context.options.context?.cloudSettings?.cloudCasS3BucketName
                  ) ||
                  !isEmpty(
                    context.options.context?.cloudSettings?.cloudCasSnsTopic
                  )
                ) {
                  return false
                }
                return true
              }
            }
          ),
    cloudCasSnsTopic: isMarketplaceCloudSettingsRevampEnabled
      ? Yup.string()
          .matches(
            emptySpaceCharsAtStartAndEndRegex,
            `${mandatoryFieldMessage}.`
          )
          .required(snsTopicArnisMandatoryWhenOtherFieldsArepresent)
          .matches(snsTopicARNPattern, casSNSTopicValidationMessage)
      : Yup.string()
          .matches(snsTopicARNPattern, casSNSTopicValidationMessage)
          .test(
            'validateSnsTopic',
            snsTopicArnisMandatoryWhenOtherFieldsArepresent,
            (value, context) => {
              if (!isEmpty(value)) {
                return true
              } else {
                if (
                  !isEmpty(
                    context.options.context?.cloudSettings?.cloudKmsKeyArn
                  ) ||
                  !isEmpty(
                    context.options.context?.cloudSettings?.cloudCasS3BucketName
                  )
                ) {
                  return false
                }
                return true
              }
            }
          ),
    currencies: Yup.array().of(Yup.string()).optional(),
    countryRegistered: Yup.string().required('Country is required'),
  })

export const cloudSettingsSchema = getCloudSettingsSchema()

export const termsAndConditionsOnboardingHelperSchema = Yup.object().shape({
  labraPartnerDetails: Yup.array()
    .of(
      Yup.object().shape({
        label: Yup.string().required(),
        value: Yup.bool().oneOf(
          [true],
          'You must check all boxes before continuing.'
        ),
        type: Yup.string().oneOf(['checkbox']).required(),
      })
    )
    .required(),
})

export const infoCollectionFormMetaDataSchema = Yup.object({
  logoUrl: Yup.string().required('Please provide a logo'),
})

export const getFlyOutValidationSchema = (
  isMarketplaceCloudSettingsRevampEnabled = false,
  isOnboardingImprovementsEnabled = false
) =>
  Yup.object().shape({
    labraPartnerDetails: labraPartnerDetailsSchema,
    cloudSettings: getCloudSettingsSchema(
      isMarketplaceCloudSettingsRevampEnabled,
      isOnboardingImprovementsEnabled
    ),
    termsAndConditions: termsAndConditionsOnboardingHelperSchema,
    infoCollectionFormMetaData: infoCollectionFormMetaDataSchema,
  })

const flyOutValidationSchema = getFlyOutValidationSchema()

export default flyOutValidationSchema
